import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../api/context";
import { ApiError } from "../api/types";
import { IconAlert, IconMinus, IconPlus } from "../components/icons";
import { HeroAmount } from "../components/motion";
import { Amount, Button, Notice } from "../components/ui";
import { useI18n } from "../i18n";
import { useBrand } from "../branding/BrandContext";
import type { MessageKey } from "../i18n/strings";
import { chainLabel } from "../lib/chains";
import { formatUnits, parseUnits } from "../lib/money";
import type { ChainId } from "../lib/types";
import { PageShell } from "./PageShell";

interface Product {
  sku: string;
  name: MessageKey;
  desc: MessageKey;
  price: string; // decimal USDC
  art: ReactNode;
}

// Plainly generic items, drawn here; no brands, no reviews, no ratings.
const PRODUCTS: Product[] = [
  {
    sku: "notebook",
    name: "demo.product.notebook",
    desc: "demo.product.notebookDesc",
    price: "12.00",
    art: (
      <svg viewBox="0 0 120 90" aria-hidden="true">
        <rect x="36" y="14" width="48" height="62" rx="4" className="art-fill" />
        <rect x="36" y="14" width="48" height="62" rx="4" className="art-line" />
        <path d="M44 14v62" className="art-line" />
        {Array.from({ length: 5 }, (_, r) =>
          Array.from({ length: 4 }, (_, c) => <circle key={`${r}-${c}`} cx={53 + c * 8} cy={27 + r * 9} r="0.9" className="art-dot" />),
        )}
        <path d="M78 14v16l3-2.5 3 2.5V14" className="art-accent" />
      </svg>
    ),
  },
  {
    sku: "mug",
    name: "demo.product.mug",
    desc: "demo.product.mugDesc",
    price: "9.50",
    art: (
      <svg viewBox="0 0 120 90" aria-hidden="true">
        <path d="M38 28h38v34a10 10 0 0 1-10 10H48a10 10 0 0 1-10-10z" className="art-fill" />
        <path d="M38 28h38v34a10 10 0 0 1-10 10H48a10 10 0 0 1-10-10z" className="art-line" />
        <path d="M76 36h5a8 8 0 0 1 0 16h-5" className="art-line" />
        <path d="M50 14c-2 3 2 5 0 8M58 12c-2 3 2 6 0 9M66 14c-2 3 2 5 0 8" className="art-accent" />
      </svg>
    ),
  },
  {
    sku: "tote",
    name: "demo.product.tote",
    desc: "demo.product.toteDesc",
    price: "18.00",
    art: (
      <svg viewBox="0 0 120 90" aria-hidden="true">
        <path d="M34 34h52l-4 42H38z" className="art-fill" />
        <path d="M34 34h52l-4 42H38z" className="art-line" />
        <path d="M48 34v-8a12 12 0 0 1 24 0v8" className="art-line" />
        <path d="M46 50h28" className="art-accent" />
      </svg>
    ),
  },
];

const DEMO_CHAINS: ChainId[] = ["solana-devnet", "base-sepolia"];

export function DemoStore() {
  const brand = useBrand();
  // A server that publishes its config and says demo mode is off has no demo merchant: say so
  // instead of offering a checkout that cannot start. Older servers (no config) are simply tried.
  if (!import.meta.env.DEV && brand.source === "server" && !brand.demoMode) return <DemoDisabled />;
  return <DemoStoreInner />;
}

function DemoDisabled() {
  const { t } = useI18n();
  return (
    <PageShell narrow>
      <main className="center-card panel" id="main">
        <span className="result-icon result-icon-neutral" aria-hidden="true">
          <IconAlert size={26} />
        </span>
        <h1 className="h1">{t("demo.disabledTitle")}</h1>
        <p className="muted">{t("demo.disabledBody")}</p>
      </main>
    </PageShell>
  );
}

function DemoStoreInner() {
  const { t } = useI18n();
  const api = useApi();
  const navigate = useNavigate();
  const [cart, setCart] = useState<Record<string, number>>({ notebook: 1, mug: 1 });
  // Only test networks this server actually has configured (known once it publishes /public/config).
  const brand = useBrand();
  const chains = useMemo(() => {
    const live = brand.chains.filter((c) => c.testnet).map((c) => c.chain);
    const offered = DEMO_CHAINS.filter((c) => live.includes(c));
    return brand.source === "server" && offered.length ? offered : DEMO_CHAINS;
  }, [brand]);
  const [chain, setChain] = useState<ChainId>(chains[0] ?? "solana-devnet");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${t("demo.title")} · DEMO`;
  }, [t]);

  const lines = PRODUCTS.filter((p) => (cart[p.sku] ?? 0) > 0).map((p) => ({ product: p, qty: cart[p.sku] ?? 0 }));
  const count = lines.reduce((n, l) => n + l.qty, 0);
  const totalBase = useMemo(
    () => lines.reduce((sum, l) => sum + BigInt(parseUnits(l.product.price, 6)) * BigInt(l.qty), 0n).toString(),
    [lines],
  );

  const change = (sku: string, delta: number) =>
    setCart((c) => {
      const next = Math.max(0, Math.min(9, (c[sku] ?? 0) + delta));
      return { ...c, [sku]: next };
    });

  const checkout = async () => {
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.createDemoInvoice({
        amount: formatUnits(totalBase, 6),
        asset: "USDC",
        chain,
        description: t("demo.orderDescription", { count }),
        items: lines.map((l) => ({ sku: l.product.sku, name: t(l.product.name), quantity: l.qty, unitPrice: l.product.price })),
      });
      navigate(`/pay/${encodeURIComponent(id)}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <PageShell
      banner={
        <div className="demo-banner" role="note">
          <IconAlert size={15} />
          <strong>{t("demo.banner")}</strong>
        </div>
      }
    >
      <main className="demo" id="main">
        <header className="demo-head">
          <span className="demo-tag">DEMO</span>
          <h1 className="h1">{t("demo.title")}</h1>
          <p className="muted">{t("demo.subtitle")}</p>
        </header>

        <div className="demo-layout">
          <ul className="products" role="list">
            {PRODUCTS.map((p) => {
              const qty = cart[p.sku] ?? 0;
              return (
                <li key={p.sku} className="product">
                  <div className="product-art">{p.art}</div>
                  <div className="product-body">
                    <div className="product-name">{t(p.name)}</div>
                    <div className="muted small">{t(p.desc)}</div>
                    <div className="product-foot">
                      <Amount value={p.price} symbol="USDC" className="product-price" />
                      {qty > 0 ? (
                        <Stepper qty={qty} name={t(p.name)} onChange={(d) => change(p.sku, d)} />
                      ) : (
                        <Button variant="secondary" onClick={() => change(p.sku, 1)}>
                          {t("demo.add")}
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <aside className="cart" aria-labelledby="cart-title">
            <h2 className="section-title" id="cart-title">
              {t("demo.cart")}
            </h2>
            {lines.length === 0 ? (
              <p className="muted small">{t("demo.cartEmpty")}</p>
            ) : (
              <ul className="cart-lines" role="list">
                {lines.map((l) => (
                  <li key={l.product.sku}>
                    <span className="cart-line-name">
                      {t(l.product.name)} <span className="muted num">× {l.qty}</span>
                    </span>
                    <Amount
                      value={formatUnits((BigInt(parseUnits(l.product.price, 6)) * BigInt(l.qty)).toString(), 6)}
                      className="cart-line-price"
                    />
                  </li>
                ))}
              </ul>
            )}
            <div className="cart-total">
              <span className="due-label">{t("demo.subtotal")}</span>
              <HeroAmount value={formatUnits(totalBase, 6)} symbol="USDC" size="md" />
            </div>

            <label className="select-field">
              <span className="field-label">{t("demo.payOn")}</span>
              <select value={chain} onChange={(e) => setChain(e.target.value as ChainId)}>
                {chains.map((c) => (
                  <option key={c} value={c}>
                    {chainLabel(c)}
                  </option>
                ))}
              </select>
            </label>

            {error ? <Notice tone="danger" title={t("demo.createFailed", { message: error })} /> : null}

            <Button size="lg" block disabled={count === 0} loading={busy} onClick={() => void checkout()}>
              {busy ? t("demo.creating") : t("demo.checkout")}
            </Button>
            <p className="muted small">{t("demo.faucet")}</p>
          </aside>
        </div>
      </main>
    </PageShell>
  );
}

function Stepper({ qty, name, onChange }: { qty: number; name: string; onChange(delta: number): void }) {
  const { t } = useI18n();
  return (
    <div className="stepper" role="group" aria-label={`${t("demo.qty")}: ${name}`}>
      <button type="button" onClick={() => onChange(-1)} aria-label={t("demo.decrease", { name })}>
        <IconMinus size={16} />
      </button>
      <span className="num" aria-live="polite">
        {qty}
      </span>
      <button type="button" onClick={() => onChange(1)} aria-label={t("demo.increase", { name })} disabled={qty >= 9}>
        <IconPlus size={16} />
      </button>
    </div>
  );
}
